/**
 * The app's themes (Settings → Styling). One choice themes everything: the
 * console, the React CRM pages and the embedded CRM frame. It is saved on the
 * person's profile with the rest of Styling (userStyling.ts) and mirrored to the
 * CRM's own `crm_theme` key, which the frame (same origin) re-applies when its
 * `storage` event fires and which paints the app before sign-in loads. Tokens
 * live in theme.css.
 */
import { useEffect, useState } from 'react';

export const THEMES = ['onlymind', 'onlymind-light', 'onlyfans-dark', 'onlyfans-light', 'aurora'] as const;
export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = 'onlymind';
export const THEME_STORAGE_KEY = 'crm_theme';

/** Swatches for the picker: each theme's page, with its accent glowing behind the rabbit. */
export const THEME_SWATCH: Record<Theme, { background: string; border?: string }> = {
  onlymind: { background: 'radial-gradient(70% 90% at 50% 70%, rgba(126, 43, 255, 0.45), transparent 70%), #0b0910' },
  'onlymind-light': { background: 'radial-gradient(70% 90% at 50% 70%, rgba(126, 43, 255, 0.18), transparent 70%), #f5f0ff', border: '#d6caec' },
  'onlyfans-dark': { background: 'radial-gradient(70% 90% at 50% 70%, rgba(0, 175, 240, 0.28), transparent 70%), #0f0f0f' },
  'onlyfans-light': { background: '#ffffff', border: '#c8cdd4' },
  aurora: { background: 'radial-gradient(70% 90% at 50% 70%, rgba(47, 196, 242, 0.28), transparent 70%), #0a141d' },
};

/** Whether the theme is drawn light (HeroUI and form controls follow it). */
export function isLightTheme(theme: Theme): boolean {
  return theme === 'onlymind-light' || theme === 'onlyfans-light';
}

/** Ids a theme was saved under before it was renamed (ADR 0044). Keep until no stored value can still say them. */
const RENAMED_THEMES: Record<string, Theme> = { dark: 'onlyfans-dark', light: 'onlyfans-light' };

/** A saved value as a theme, or undefined when it is retired (nebula, culturie, fantasy) or unknown. */
export function parseTheme(value: unknown): Theme | undefined {
  const code = String(value ?? '').trim().toLowerCase();
  if ((THEMES as readonly string[]).includes(code)) return code as Theme;
  return RENAMED_THEMES[code];
}

/** A saved value as a theme: renamed ids map to their new id; anything retired or unknown is the default. */
export function normalizeTheme(value: unknown): Theme {
  return parseTheme(value) ?? DEFAULT_THEME;
}

export function readTheme(): Theme {
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

/** Paint the document with a theme. */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  const light = isLightTheme(theme);
  const switching = root.dataset.omTheme !== undefined && root.dataset.omTheme !== theme;
  if (switching) root.classList.add('om-theme-switching');
  root.dataset.omTheme = theme;
  // HeroUI's own switch.
  root.dataset.theme = light ? 'light' : 'dark';
  root.classList.toggle('dark', !light);
  root.classList.toggle('light', light);
  if (switching) {
    // Let the new colours land before transitions are allowed again.
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => root.classList.remove('om-theme-switching')));
  }
}

const listeners = new Set<(theme: Theme) => void>();

/** Save and apply a theme. Returns false when storage is unavailable (it still applies for this page). */
export function setTheme(theme: Theme): boolean {
  const next = normalizeTheme(theme);
  applyTheme(next);
  listeners.forEach((fn) => fn(next));
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
    return true;
  } catch {
    return false;
  }
}

/**
 * Apply the saved theme at start-up and keep following it when it changes in
 * another tab or in the CRM frame. A retired theme is rewritten as the default,
 * and a renamed one under its new id, so the frame, which reads the same key,
 * shows the same theme.
 */
export function initTheme(): () => void {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
  const theme = normalizeTheme(raw);
  if (raw !== null && raw !== theme) {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }
  applyTheme(theme);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== THEME_STORAGE_KEY) return;
    const next = readTheme();
    applyTheme(next);
    listeners.forEach((fn) => fn(next));
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** The theme in use, following changes from anywhere. */
export function useTheme(): [Theme, (theme: Theme) => boolean] {
  const [theme, setState] = useState<Theme>(readTheme);
  useEffect(() => {
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, []);
  return [theme, setTheme];
}
